import { z } from 'zod';
import { PAPERCUT_WASTE_QUANTIFICATION_VERSION } from '../types/papercut-waste-quantification';

const dollarsSchema = z.number().min(0);

const wasteCostOutputSchema = z.object({
  totalCostDollars: dollarsSchema,
});

const timeWasteDirectCalculationSchema = z.object({
  method: z.literal('direct'),
  minutesPerOccurrence: z.number().int().min(0),
  reachPeopleCount: z.number().int().positive(),
  laborRateDollarsPerHour: z.number().min(0),
  occurrencesPerYear: z.number().positive().optional().default(52),
  note: z.string().optional(),
});

const timeWasteComposedLineSchema = z.object({
  label: z.string().optional(),
  minutes: z.number().min(0),
  multiplier: z.number().min(0),
});

const timeWasteComposedCalculationSchema = z.object({
  method: z.literal('composed'),
  description: z.string().optional(),
  lines: z.array(timeWasteComposedLineSchema).min(1),
  reachPeopleCount: z.number().int().positive(),
  laborRateDollarsPerHour: z.number().min(0),
  occurrencesPerYear: z.number().positive().optional().default(52),
});

export const papercutTimeWasteSchema = z.object({
  version: z.literal(PAPERCUT_WASTE_QUANTIFICATION_VERSION),
  output: wasteCostOutputSchema,
  calculation: z.discriminatedUnion('method', [
    timeWasteDirectCalculationSchema,
    timeWasteComposedCalculationSchema,
  ]),
});

const materialNoneSchema = z.object({ method: z.literal('none') });
const materialDirectSchema = z.object({
  method: z.literal('direct'),
  dollars: dollarsSchema,
  occurrencesPerYear: z.number().positive().optional().default(52),
  note: z.string().optional(),
});
const materialLineItemsSchema = z.object({
  method: z.literal('line_items'),
  lineItems: z.array(
    z.object({
      label: z.string().optional(),
      quantity: z.number().min(0),
      unitCostDollars: dollarsSchema,
    }),
  ).min(1),
  occurrencesPerYear: z.number().positive().optional().default(52),
});

export const papercutMaterialWasteSchema = z.object({
  version: z.literal(PAPERCUT_WASTE_QUANTIFICATION_VERSION),
  output: wasteCostOutputSchema,
  calculation: z.discriminatedUnion('method', [
    materialNoneSchema,
    materialDirectSchema,
    materialLineItemsSchema,
  ]),
});

const capitalNoneSchema = z.object({ method: z.literal('none') });
const capitalDirectSchema = z.object({
  method: z.literal('direct'),
  dollars: dollarsSchema,
  occurrencesPerYear: z.number().positive().optional().default(1),
  note: z.string().optional(),
});
const capitalDowntimeSchema = z.object({
  method: z.literal('downtime'),
  assetLabel: z.string().optional(),
  assetValueDollars: dollarsSchema,
  utilizationRate: z.number().min(0).max(1),
  productiveHoursPerYear: z.number().positive(),
  hoursDown: z.number().min(0),
  occurrencesPerYear: z.number().positive().optional().default(1),
});
const capitalLifeShortenedSchema = z.object({
  method: z.literal('life_shortened'),
  assetLabel: z.string().optional(),
  replacementCostDollars: dollarsSchema,
  expectedLifeYears: z.number().positive(),
  lifeImpactYears: z.number().min(0),
});
const capitalOpportunityRevenueSchema = z.object({
  method: z.literal('opportunity_revenue'),
  assetLabel: z.string().optional(),
  revenuePerMonthDollars: dollarsSchema.optional(),
  monthsUnavailable: z.number().min(0).optional(),
  /** Legacy day-based inputs — still accepted for existing JSON */
  revenuePerDayDollars: dollarsSchema.optional(),
  daysUnavailable: z.number().min(0).optional(),
});

export const papercutCapitalWasteSchema = z.object({
  version: z.literal(PAPERCUT_WASTE_QUANTIFICATION_VERSION),
  output: wasteCostOutputSchema,
  calculation: z.discriminatedUnion('method', [
    capitalNoneSchema,
    capitalDirectSchema,
    capitalDowntimeSchema,
    capitalLifeShortenedSchema,
    capitalOpportunityRevenueSchema,
  ]),
});

export const papercutWasteQuantificationInputSchema = z.object({
  timeWaste: papercutTimeWasteSchema.optional(),
  materialWaste: papercutMaterialWasteSchema.optional(),
  capitalWaste: papercutCapitalWasteSchema.optional(),
  /** Legacy convenience — converted server-side to timeWaste when timeWaste omitted */
  timeCostMinutes: z.number().int().min(0).optional(),
  peopleAffected: z.number().int().positive().optional(),
});
